import { useMemo } from "react";
import { useStore } from "ra-core";
import { toSlug } from "@/lib/toSlug";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  googleWorkplaceDomain?: string;
  ssoProvider?: string;
  ssoLabel?: string;
  disableEmailPasswordAuthentication?: boolean;
}

const ensureValues = <T extends { label: string; value?: string | null }>(
  items: T[] | undefined,
): T[] | undefined =>
  items?.map((item) => ({
    ...item,
    value: item.value || toSlug(item.label),
  }));

const normalizeConfiguration = (
  config: ConfigurationContextValue,
): ConfigurationContextValue => ({
  ...config,
  companySectors: ensureValues(config.companySectors) ?? config.companySectors,
  dealCategories: ensureValues(config.dealCategories) ?? config.dealCategories,
  dealStages: ensureValues(config.dealStages) ?? config.dealStages,
  noteStatuses: ensureValues(config.noteStatuses) ?? config.noteStatuses,
  taskTypes: ensureValues(config.taskTypes) ?? config.taskTypes,
  dealPipelineStatuses: config.dealPipelineStatuses?.filter(Boolean) ?? [],
});

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(
    () =>
      normalizeConfiguration({
        ...defaultConfiguration,
        ...config,
        ssoProvider: config.ssoProvider ?? import.meta.env.VITE_SSO_PROVIDER,
        ssoLabel: config.ssoLabel ?? import.meta.env.VITE_SSO_LABEL,
        googleWorkplaceDomain:
          config.googleWorkplaceDomain ??
          import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
      }),
    [config],
  );
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
